import type { PageContextEnvelope } from 'src/components/PageContext'

import { composePageContextText } from './compose-page-context-text'
import { trimToMax } from './utils'

const MAX_HTML_CONTEXT_CHARS = 8_000
const SENSITIVE_TEXT_PATTERN =
  /\b(password|passcode|ssn|social security|signature|credit card|card number|cvv|routing number|bank account|tax id|ein|driver'?s license|passport|iban|swift|otp|one[-\s]?time code|security code)\b/i

const ALLOWED_ATTRIBUTES = new Set([
  'aria-describedby',
  'aria-label',
  'aria-labelledby',
  'disabled',
  'href',
  'name',
  'placeholder',
  'required',
  'role',
  'title',
  'type',
])
const SENSITIVE_AUTOCOMPLETE_VALUES = new Set([
  'cc-number',
  'cc-csc',
  'cc-cvc',
  'cc-exp',
  'cc-exp-month',
  'cc-exp-year',
  'one-time-code',
  'password',
  'new-password',
  'current-password',
])

const normalizeWhitespace = (text: string): string => {
  return text.replace(/\s+/g, ' ').trim()
}

const removeNodes = (rootNode: ParentNode): void => {
  const removableSelector = [
    'script',
    'style',
    'noscript',
    'svg',
    'canvas',
    'iframe',
    'img',
    'video',
    'audio',
    'source',
    'link',
    'meta',
    '[hidden]',
    '[aria-hidden="true"]',
    'input[type="hidden"]',
    '[data-sensitive]',
    '[data-private]',
    '[data-field-type="ForeignValue"]',
  ].join(', ')

  for (const element of rootNode.querySelectorAll(removableSelector)) {
    element.remove()
  }

  if (rootNode instanceof Element && rootNode.matches(removableSelector)) {
    rootNode.innerHTML = ''
  }
}

const isSensitiveElement = (element: Element): boolean => {
  const sensitiveAttributes = ['aria-label', 'name', 'placeholder', 'title', 'id']

  for (const attrName of sensitiveAttributes) {
    if (SENSITIVE_TEXT_PATTERN.test(element.getAttribute(attrName) ?? '')) {
      return true
    }
  }

  const autocomplete = element.getAttribute('autocomplete')?.toLowerCase()
  if (autocomplete && SENSITIVE_AUTOCOMPLETE_VALUES.has(autocomplete)) {
    return true
  }

  const inputType = element.getAttribute('type')?.toLowerCase()
  if (inputType === 'password') {
    return true
  }

  return false
}

const stripUnsafeAttributes = (rootNode: ParentNode): void => {
  for (const element of rootNode.querySelectorAll('*')) {
    const tagName = element.tagName.toLowerCase()
    if (isSensitiveElement(element)) {
      element.remove()
      continue
    }

    // Remove form controls entirely to avoid leaking any typed or autofilled values.
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      element.remove()
      continue
    }

    if (element.getAttribute('contenteditable') === 'true') {
      element.remove()
      continue
    }

    for (const attr of [...element.attributes]) {
      const attrName = attr.name.toLowerCase()
      if (attrName.startsWith('on') || attrName.startsWith('data-')) {
        element.removeAttribute(attr.name)
        continue
      }
      if (!ALLOWED_ATTRIBUTES.has(attrName)) {
        element.removeAttribute(attr.name)
      }
    }
  }
}

const getPrimaryContainer = (): HTMLElement => {
  return (
    (document.querySelector('main') as HTMLElement | null) ??
    (document.querySelector('[role="main"]') as HTMLElement | null) ??
    document.body
  )
}

type BuildHtmlPageContextArgs = {
  viewer: 'citizen' | 'staff'
  structuredPageContextText?: string
  structuredPageContextEnvelope?: PageContextEnvelope
}

export const buildHtmlPageContext = (args: BuildHtmlPageContextArgs): string => {
  const { viewer, structuredPageContextText, structuredPageContextEnvelope } = args
  const routePath = window.location.pathname
  const primaryContainer = getPrimaryContainer()
  const clonedContainer = primaryContainer.cloneNode(true) as HTMLElement

  removeNodes(clonedContainer)
  stripUnsafeAttributes(clonedContainer)

  return composePageContextText({
    routePath,
    viewer,
    sanitizedHtml: trimToMax(
      normalizeWhitespace(clonedContainer.innerHTML),
      MAX_HTML_CONTEXT_CHARS
    ),
    structuredPageContextText,
    structuredPageContextEnvelope,
  })
}
